import { FC, MouseEventHandler, memo, useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";

import { ICannedResponseTagValue, ICannedResponsesProps } from "components/message-box/canned-responses";
import { KeyCodeEnum } from "constants/keyCode";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { createNewEvent } from "helpers/events";

import { CannedResponseItem } from "./CannedResponseItem";
import { CannedResponsesDropdown } from "./CannedResponsesDropdown";
import { CannedResponsesList } from "./CannedResponsesList";
import { CannedResponsesTagList } from "./CannedResponsesTagList";

export const CannedResponses: FC<ICannedResponsesProps> = memo(
  ({
    className,
    items,
    onClose,
    onSearchResultsCountChange,
    onSelect,
    searchPhrase,
    textareaWithCannedResponsesRef,
    containerRef,
  }) => {
    const [selectedTag, selectTag] = useState<string | null>(null);
    const areCannedResponsesVisible = !!selectedTag;

    const handleResponseSelect = useCallback(
      (
        cannedResponseTagValue: ICannedResponseTagValue,
        event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement> | KeyboardEvent
      ) => {
        selectTag(null);
        onSelect(cannedResponseTagValue, event.type === "keydown" ? "keyboard" : "mouse");
        trackEngagement("Editor: Canned response used", EventPlace.RichTextEditor);

        // trigger custom event for chat tutorial
        const cannedCustomEvent = createNewEvent("canned_response_used");
        document.dispatchEvent(cannedCustomEvent);
      },
      [onSelect]
    );

    const handleClose = useCallback(() => {
      selectTag(null);
      onClose();
    }, [onClose]);

    const handleKeydown = useCallback(
      (e: KeyboardEvent) => {
        if (areCannedResponsesVisible && e.keyCode === KeyCodeEnum.ArrowLeft) {
          e.preventDefault();
          selectTag(null);
        }
      },
      [areCannedResponsesVisible]
    );

    const handleLeftArrowClick: MouseEventHandler<HTMLButtonElement> & MouseEventHandler<HTMLAnchorElement> = e => {
      // Prevent click outside
      e.nativeEvent.stopImmediatePropagation();
      // Prevent onSelect
      e.stopPropagation();
      selectTag(null);
    };

    const handleSearchResultsCountChange = useCallback(
      (count: number) => {
        onSearchResultsCountChange?.(count);

        if (count === 0) {
          selectTag(null);

          /*
            We want to reopen canned responses as in case of no results
            They're not visible and user might want to search again.
            Fully close canneds only when there is no results and user used space as last search phrase symbol
          */
          if (searchPhrase.endsWith(" ")) {
            onClose();
          }
        }
      },
      [onSearchResultsCountChange, onClose, searchPhrase]
    );

    const cannedItems = useMemo(
      () =>
        areCannedResponsesVisible
          ? (items
              .find(item => item.name === selectedTag)
              ?.values.map((item, index) => ({
                itemId: item.text,
                content: (
                  <CannedResponseItem
                    name={item.text}
                    onArrowClick={handleLeftArrowClick}
                    testSelector={`canned-response-item-${index}`}
                  />
                ),
                onItemSelect: (
                  _: string,
                  e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement> | KeyboardEvent
                ): void => handleResponseSelect(item, e),
              })) ?? [])
          : [],
      [areCannedResponsesVisible, items, handleResponseSelect, selectedTag]
    );

    useEffect(() => {
      document.addEventListener("keydown", handleKeydown);

      return (): void => {
        document.removeEventListener("keydown", handleKeydown);
      };
    }, [handleKeydown]);

    useLayoutEffect(() => {
      const cannedCustomEvent = createNewEvent("canned_responses_updated");
      document.dispatchEvent(cannedCustomEvent);
    });

    return (
      <CannedResponsesDropdown
        ref={containerRef}
        className={className}
        onClose={handleClose}
        textareaWithCannedResponsesRef={textareaWithCannedResponsesRef}
      >
        <CannedResponsesTagList
          tags={items}
          searchPhrase={searchPhrase}
          isVisible={!areCannedResponsesVisible}
          onClose={onClose}
          onSearchResultsCountChange={handleSearchResultsCountChange}
          onSelect={selectTag}
        />
        {areCannedResponsesVisible && <CannedResponsesList items={cannedItems} />}
      </CannedResponsesDropdown>
    );
  }
);

CannedResponses.displayName = "CannedResponses";
